import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox } from '@/shared/inputs';
import { Text } from '@/shared/ui';
import { Spacing, Colors } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import FormControlLabel from '@mui/material/FormControlLabel';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

// Boris "rg-consent-row": rows in the rg-consent-doc card are separated by a
// hairline divider, with the last row's divider omitted. Purely presentational.
const CheckboxWithLabelContainer = styled(FormControlLabel)<{ $isLastRow?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${Spacing.x1_5}px;
  margin: 0;
  width: 100%;
  padding: 14px 0;
  border-bottom-style: solid;
  border-bottom-color: ${BorisScreener.line};
  border-bottom-width: ${({ $isLastRow }) => ($isLastRow ? '0px' : '1px')};
`;

// Boris rg-consent-desc: warm muted ink (#5C5F61), not the app's cool hint gray.
const DisclaimerText = styled(Text)`
  color: ${BorisScreener.inkSoft};
`;

// Boris rg-consent-title .req: coral asterisk marking a required consent.
const Req = styled.span`
  color: ${BorisScreener.coral};
`;

const DisclaimerTextLink = styled(Text)`
  font-weight: 700;
  text-decoration: underline;
  color: ${Colors.primary};
  cursor: pointer;
`;

const DisclaimerLink = styled.a`
  text-decoration: none;
  cursor: pointer;
`;

export type Disclaimers = {
  enableSmsMarketing: boolean;
  privacyPracticeReviewed: boolean;
  consentToShareData: boolean;
};

type Props = {
  disclaimers: Disclaimers;
  setDisclaimers: React.Dispatch<React.SetStateAction<Disclaimers>>;
  onPrivacyClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onInformedConsentClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  isCareForHeartConsentRequired?: boolean;
};

const DisclaimersCheckboxes: React.FC<Props> = ({
  disclaimers,
  setDisclaimers,
  onPrivacyClick,
  onInformedConsentClick,
  isCareForHeartConsentRequired = false,
}) => {
  const { t } = useTranslation();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisclaimers({ ...disclaimers, [event.target.name]: event.target.checked });
  };

  const handlePrivacyClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (onPrivacyClick) {
      onPrivacyClick(e);
    }
  };

  const handleInformedConsentClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (onInformedConsentClick) {
      onInformedConsentClick(e);
    }
  };

  return (
    <Container>
      {/* SMS Marketing Consent */}
      <CheckboxWithLabelContainer
        control={
          <Checkbox
            name="enableSmsMarketing"
            checked={disclaimers.enableSmsMarketing}
            onChange={handleChange}
            sx={{ alignSelf: 'start', padding: 0 }}
            inputProps={{
              'aria-label': t('create-account.disclaimers.sms-consent'),
            }}
          />
        }
        label={
          <DisclaimerText size="p3">
            {t('create-account.disclaimers.sms-consent')}
          </DisclaimerText>
        }
      />
      {/* Privacy Practice & Care for Heart Consent */}
      <CheckboxWithLabelContainer
        $isLastRow
        control={
          <Checkbox
            name="privacyPracticeReviewed"
            checked={disclaimers.privacyPracticeReviewed}
            onChange={handleChange}
            sx={{ alignSelf: 'start', padding: 0 }}
            inputProps={{
              'aria-label': t(
                isCareForHeartConsentRequired
                  ? 'create-account.disclaimers.read-heart-consent'
                  : 'create-account.disclaimers.read-privacy-practices'
              ),
              'aria-required': true,
            }}
          />
        }
        label={
          <DisclaimerLink
            role="button"
            aria-label={t(
              isCareForHeartConsentRequired
                ? 'create-account.disclaimers.read-heart-consent'
                : 'create-account.disclaimers.read-privacy-practices'
            )
              .replace(/<[^>]*>/g, '')
              .trim()}
          >
            {isCareForHeartConsentRequired ? (
              <Trans
                i18nKey="create-account.disclaimers.read-heart-consent"
                components={{
                  1: <DisclaimerText size="p3" />,
                  2: (
                    <DisclaimerTextLink
                      size="p3"
                      onClick={handlePrivacyClick}
                      onKeyDown={(e) => e.key === 'Enter' && handlePrivacyClick(e)}
                      role="link"
                      tabIndex={0}
                    />
                  ),
                  3: (
                    <DisclaimerTextLink
                      size="p3"
                      onClick={handleInformedConsentClick}
                      onKeyDown={(e) => e.key === 'Enter' && handleInformedConsentClick(e)}
                      role="link"
                      tabIndex={0}
                    />
                  ),
                }}
              />
            ) : (
              <Trans
                i18nKey="create-account.disclaimers.read-privacy-practices"
                components={{
                  1: <DisclaimerText size="p3" />,
                  2: (
                    <DisclaimerTextLink
                      size="p3"
                      onClick={handlePrivacyClick}
                      onKeyDown={(e) => e.key === 'Enter' && handlePrivacyClick(e)}
                      role="link"
                      tabIndex={0}
                    />
                  ),
                }}
              />
            )}
            <Req aria-hidden="true"> *</Req>
          </DisclaimerLink>
        }
      />
    </Container>
  );
};

export default DisclaimersCheckboxes;
